import { api, API_BASE } from "@/lib/apiClient";

export type TagesgeschaeftCandidateStatus = "pending" | "confirmed" | "dismissed";

export interface TagesgeschaeftMailbox {
  id: number;
  mailbox_upn: string;
  ordner_id: number;
  label?: string | null;
  enabled: boolean;
  graph_folder?: string | null;
  ordner_nummer?: string | null;
  ordner_beschreibung?: string | null;
  ordner_jahr?: number | null;
  kunden_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface TagesgeschaeftFieldMappingItem {
  id?: number;
  mailbox_id?: number;
  field_key: string;
  label?: string | null;
  enabled: boolean;
  extraction_hint?: string | null;
  example_value?: string | null;
  sort_order?: number;
}

export interface TagesgeschaeftFieldCatalogItem {
  field_key: string;
  label: string;
  enabled: boolean;
  extraction_hint?: string;
  sort_order?: number;
}

export interface TagesgeschaeftAttachmentSummary {
  id?: string | null;
  name?: string | null;
  size?: number | null;
  contentType?: string | null;
  odata_type?: string | null;
}

export interface TagesgeschaeftCandidate {
  id: number;
  mailbox_id: number;
  ordner_id: number;
  mailbox: string;
  mailbox_label?: string | null;
  graph_message_id?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  subject?: string | null;
  received_at?: string | null;
  snippet?: string | null;
  has_attachments?: boolean;
  attachments_summary?: TagesgeschaeftAttachmentSummary[] | null;
  extracted_fields?: Record<string, string | null> | null;
  extraction_confidence?: Record<string, number> | null;
  status: TagesgeschaeftCandidateStatus;
  ordner_nummer?: string | null;
  ordner_beschreibung?: string | null;
  ordner_jahr?: number | null;
  kunden_name?: string | null;
  created_auftrag_id?: number | null;
  beauftragung_filename?: string | null;
  beauftragung_sharepoint_item_id?: string | null;
  beauftragung_sharepoint_web_url?: string | null;
  confirmed_at?: string | null;
  dismissed_at?: string | null;
}

export interface TagesgeschaeftProjectPrefill {
  candidate_id: number;
  ordner_prefill: {
    type: "projekt";
    ordnerNummer?: string | null;
    jahr?: number | null;
    kunde?: string | null;
  };
  form_prefill: {
    beschreibung?: string;
    ansprechpartner?: string;
    ansprechpartner_email?: string;
    adresse?: string;
    ort_zusatz?: string;
  };
  beauftragung: {
    source?: string;
    attachment_hint?: string | null;
    attachment_id?: string | null;
    has_attachments?: boolean;
  };
  candidate: TagesgeschaeftCandidate;
}

export async function listTagesgeschaeftMailboxes(): Promise<TagesgeschaeftMailbox[]> {
  const res = await api.get<{ items: TagesgeschaeftMailbox[] }>("/tagesgeschaeft/mailboxes");
  return res.items || [];
}

export async function createTagesgeschaeftMailbox(body: {
  mailbox_upn: string;
  ordner_id: number;
  label?: string | null;
  enabled?: boolean;
}): Promise<TagesgeschaeftMailbox> {
  return api.post<TagesgeschaeftMailbox>("/tagesgeschaeft/mailboxes", body);
}

export async function patchTagesgeschaeftMailbox(
  id: number,
  body: Partial<{
    mailbox_upn: string;
    ordner_id: number;
    label: string | null;
    enabled: boolean;
  }>,
): Promise<TagesgeschaeftMailbox> {
  return api.patch<TagesgeschaeftMailbox>(`/tagesgeschaeft/mailboxes/${id}`, body);
}

export async function deleteTagesgeschaeftMailbox(id: number): Promise<void> {
  await api.del(`/tagesgeschaeft/mailboxes/${id}`);
}

export async function getTagesgeschaeftMapping(mailboxId: number): Promise<{
  mailbox_id: number;
  mailbox_upn: string;
  field_catalog: TagesgeschaeftFieldCatalogItem[];
  items: TagesgeschaeftFieldMappingItem[];
}> {
  return api.get(`/tagesgeschaeft/mailboxes/${mailboxId}/mapping`);
}

export async function putTagesgeschaeftMapping(
  mailboxId: number,
  items: TagesgeschaeftFieldMappingItem[],
): Promise<{
  mailbox_id: number;
  items: TagesgeschaeftFieldMappingItem[];
}> {
  return api.put(`/tagesgeschaeft/mailboxes/${mailboxId}/mapping`, {
    items: items.map((i) => ({
      field_key: i.field_key,
      enabled: i.enabled,
      extraction_hint: i.extraction_hint ?? null,
      example_value: i.example_value ?? null,
      sort_order: i.sort_order ?? 0,
    })),
  });
}

export async function suggestTagesgeschaeftMapping(mailboxId: number): Promise<{
  sample_count: number;
  ai_enabled: boolean;
  suggestions: TagesgeschaeftFieldMappingItem[];
}> {
  return api.post(`/tagesgeschaeft/mailboxes/${mailboxId}/suggest-mapping`);
}

export async function scanTagesgeschaeftMailbox(
  mailboxId: number,
  options?: { days?: number; max_items?: number },
): Promise<{ ok: boolean; scanned: number }> {
  const q = new URLSearchParams();
  if (options?.days != null) q.set("days", String(options.days));
  if (options?.max_items != null) q.set("max_items", String(options.max_items));
  const qs = q.toString();
  return api.post(`/tagesgeschaeft/mailboxes/${mailboxId}/scan${qs ? `?${qs}` : ""}`);
}

export async function listTagesgeschaeftCandidates(
  status?: TagesgeschaeftCandidateStatus,
  mailboxId?: number,
): Promise<{
  items: TagesgeschaeftCandidate[];
  counts: { pending: number; confirmed: number; dismissed: number; total: number };
  live_monitor?: boolean;
  ai_enabled?: boolean;
}> {
  const q = new URLSearchParams();
  if (status) q.set("status", status);
  if (mailboxId != null) q.set("mailbox_id", String(mailboxId));
  const qs = q.toString();
  return api.get(`/tagesgeschaeft/candidates${qs ? `?${qs}` : ""}`);
}

export async function patchTagesgeschaeftCandidateFields(
  id: number,
  extracted_fields: Record<string, string | null>,
): Promise<TagesgeschaeftCandidate> {
  return api.patch(`/tagesgeschaeft/candidates/${id}/fields`, { extracted_fields });
}

export async function reanalyzeTagesgeschaeftCandidate(id: number): Promise<TagesgeschaeftCandidate> {
  return api.post(`/tagesgeschaeft/candidates/${id}/reanalyze`);
}

export async function dismissTagesgeschaeftCandidate(
  id: number,
  note?: string,
): Promise<TagesgeschaeftCandidate> {
  return api.post(`/tagesgeschaeft/candidates/${id}/dismiss`, { note: note || null });
}

export async function getTagesgeschaeftProjectPrefill(id: number): Promise<TagesgeschaeftProjectPrefill> {
  return api.get(`/tagesgeschaeft/candidates/${id}/project-prefill`);
}

export async function completeTagesgeschaeftCandidate(
  id: number,
  body: {
    auftrag_id: number;
    beauftragung_source?: string | null;
    selected_attachment_id?: string | null;
  },
): Promise<{
  ok: boolean;
  candidate: TagesgeschaeftCandidate;
  beauftragung?: { filename?: string; sharepoint_web_url?: string };
}> {
  return api.post(`/tagesgeschaeft/candidates/${id}/complete`, body);
}

export function tagesgeschaeftAttachmentUrl(candidateId: number, attachmentId: string): string {
  return `${API_BASE || ""}/tagesgeschaeft/candidates/${candidateId}/attachments/${encodeURIComponent(attachmentId)}`;
}

export interface StoredMailBeauftragung {
  id: number;
  subject?: string | null;
  from_name?: string | null;
  from_email?: string | null;
  filename?: string | null;
  sharepoint_web_url?: string | null;
  confirmed_at?: string | null;
}

export function isStoredMailBeauftragung(filename?: string | null): boolean {
  const name = String(filename || "").trim().toLowerCase();
  return name.endsWith(".eml") || name.endsWith(".msg") || name.endsWith(".txt");
}

export async function listAuftragMailBeauftragungen(
  auftragId: number,
): Promise<StoredMailBeauftragung[]> {
  const res = await api.get<{ items?: StoredMailBeauftragung[] }>(
    `/tagesgeschaeft/beauftragungen?auftrag_id=${encodeURIComponent(String(auftragId))}`,
  );
  return Array.isArray(res.items) ? res.items : [];
}

function messageFromResponse(text: string, status: number): string {
  const trimmed = text.trim();
  if (trimmed.startsWith("{")) {
    try {
      const body = JSON.parse(trimmed) as { detail?: unknown };
      if (typeof body.detail === "string" && body.detail.trim()) return body.detail.trim();
    } catch {
      /* Rohtext */
    }
  }
  return trimmed || `Fehler ${status}`;
}

/** Abgelegte Beauftragung laden. Mails kommen als lesbares PDF. */
export async function fetchTagesgeschaeftBeauftragung(candidateId: number): Promise<Blob> {
  const res = await fetch(
    `${API_BASE || ""}/tagesgeschaeft/candidates/${candidateId}/beauftragung`,
    { credentials: "include" },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(messageFromResponse(text, res.status));
  }
  return res.blob();
}
